import { Grid, Stack } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { FormSelect } from "components/Forms/FormSelect";
import { FormTextField } from "components/Forms/FormTextField";
import { WasteProfileTestType } from "generated-graphql/graphql";
import { DateTime } from "luxon";
import { Controller, useForm } from "react-hook-form";

import { useAddWasteProfileTest, useEditWasteProfileTest } from "./api";
import { prettyPrintTestType } from "./profileDisplay";
import { WasteProfileTestData } from "./types";

interface TestFormValues {
  testDate: DateTime | null;
  type: WasteProfileTestType;
  method: string;
  labName: string;
  resultSummary: string;
}

const testTypeItems = Object.values(WasteProfileTestType).map((value) => ({
  value,
  display: prettyPrintTestType(value) ?? value,
}));

const emptyValues: TestFormValues = {
  testDate: null,
  type: WasteProfileTestType.LabAnalysis,
  method: "",
  labName: "",
  resultSummary: "",
};

const toValues = (test: WasteProfileTestData | null): TestFormValues =>
  test
    ? {
        // `testDate` is a `Date` scalar (YYYY-MM-DD); parsing it as UTC keeps
        // the picker on the stored calendar day for users west of UTC.
        testDate: DateTime.fromISO(test.testDate, { zone: "utc" }),
        type: test.type,
        method: test.method ?? "",
        labName: test.labName ?? "",
        resultSummary: test.resultSummary ?? "",
      }
    : emptyValues;

const nullableString = (value: string) => value?.trim() || null;

/**
 * One dialog for both add and edit: the two mutations take the same fields,
 * differing only in whether an `id` rides along.
 */
export const WasteProfileTestDialog = ({
  open,
  onClose,
  wasteStreamId,
  test,
}: {
  open: boolean;
  onClose: () => void;
  wasteStreamId: string;
  /** Null when adding. */
  test: WasteProfileTestData | null;
}) => {
  const alerts = useAlerts();
  const { control, handleSubmit } = useForm<TestFormValues>({
    defaultValues: toValues(test),
  });
  const [addTest, { loading: adding }] = useAddWasteProfileTest();
  const [editTest, { loading: editing }] = useEditWasteProfileTest();
  const saving = adding || editing;

  const onSubmit = async (values: TestFormValues) => {
    if (!values.testDate?.isValid) {
      alerts.error("Enter a valid test date");
      return;
    }
    // The `Date` scalar wants a calendar day, not an instant. Formatting the
    // picker's own value avoids the timezone shift an ISO instant would add.
    const testDate = values.testDate.toFormat("yyyy-MM-dd");
    const shared = {
      wasteStreamId,
      testDate,
      type: values.type,
      method: nullableString(values.method),
      labName: nullableString(values.labName),
      resultSummary: nullableString(values.resultSummary),
    };

    try {
      if (test) {
        // editWasteProfileTest is a full replace of the test row, and it
        // coerces an omitted resultDocumentId to null. This form has no
        // control for that field — Project 7 sets it from an extracted lab
        // report — so the current value is echoed back deliberately.
        // Dropping it would silently detach the report on any edit.
        await editTest({
          variables: {
            input: { ...shared, id: test.id, resultDocumentId: test.resultDocument?.id ?? null },
          },
        });
        alerts.success("Test updated");
      } else {
        await addTest({ variables: { input: shared } });
        alerts.success("Test recorded");
      }
      onClose();
    } catch (error) {
      alerts.error("Failed to save test", error instanceof Error ? error.message : undefined);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      title={test ? "Edit test" : "Add test"}
      primaryActionButtonProps={{ loading: saving, onClick: handleSubmit(onSubmit) }}
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="testDate"
              control={control}
              render={({ field: { value, onChange } }) => (
                <DatePicker
                  label="Test date"
                  value={value}
                  onChange={onChange}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormSelect
              name="type"
              label="Type"
              control={control}
              hideErrors
              selectItems={testTypeItems}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormTextField name="method" label="Method" control={control} fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormTextField name="labName" label="Lab" control={control} fullWidth />
          </Grid>
          <Grid size={12}>
            <FormTextField
              name="resultSummary"
              label="Result summary"
              control={control}
              fullWidth
              textFieldProps={{ multiline: true, minRows: 2 }}
            />
          </Grid>
        </Grid>
      </Stack>
    </Dialog>
  );
};
